import { getErrorMessage, isBackendError } from './errors';
import { createLogger } from './logger';

const logger = createLogger('API');

/**
 * 标准 API 响应结构。
 *
 * 治理变更：默认泛型从 `any` 改为 `unknown`。
 * 这样调用方在 `ApiResponse`（不带泛型）后续访问 `.data` 时会被强制类型守卫，
 * 避免静默继承 `any` 失去类型检查。
 */
export interface ApiResponse<T = unknown> {
  status: 'success' | 'fail' | 'error';
  data?: T;
  message?: string;
  code?: string;
  timestamp?: string;
  trace_id?: string;
}

/**
 * 断言 API 响应成功。
 *
 * 使用 `asserts` 类型谓词：assert 通过后，TS 会把 response 窄化为 ApiResponse 形状，
 * 后续访问 `response.data` / `response.message` 可直接获得类型提示，无需再做守卫。
 *
 * @throws Error 当 response.status !== 'success' 时
 */
export function assertSuccess(
  response: unknown,
  fallbackMessage = '操作失败',
): asserts response is ApiResponse<unknown> {
  if (isApiResponseLike(response) && response.status === 'success') {
    return;
  }

  const message =
    (isApiResponseLike(response) && typeof response.message === 'string'
      ? response.message
      : undefined) || fallbackMessage;

  logger.error(message);
  throw new Error(message);
}

/**
 * 判断值是否是非空对象（plain object / array / class instance 皆可）。
 *
 * 用于从未知响应包裹中安全读取字段前的初步守卫。返回 `Record<string, unknown>`
 * 后，调用方可以直接访问任意 key（得到 `unknown`），再用 `pickString` / `pickCode`
 * 等 helper 进一步窄化。
 */
export function isPlainObject(
  value: unknown,
): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * 若值是字符串则原样返回，否则返回 undefined。
 * 用于从未知响应对象中安全提取 `message` / `timestamp` / `trace_id` 等字段。
 */
export function pickString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

/**
 * 若值是字符串或数字则原样返回，否则返回 null。
 * 用于从未知响应对象中安全提取业务错误码（后端可能是字符串或数值）。
 */
export function pickCode(value: unknown): string | number | null {
  return typeof value === 'string' || typeof value === 'number' ? value : null;
}

/**
 * Service 层 `normalizeResponse` / `handleError` 在"响应完全无法解析"的兜底
 * 分支下，需要一个类型兼容的 `defaultData` 占位值。由于大多数响应的 `data`
 * 字段是非空业务实体（如 `MedicationPlan`），这里用 `null` 做运行时占位，再
 * 借 TypeScript 上下文推断把返回值窄化到调用处指定的 `T`。
 *
 * 调用方约定：必须先检查 `response.status === 'error'`，不要直接读 `.data`。
 */
export function errorPlaceholder<T>(): T {
  return null as unknown as T;
}

/**
 * 判断对象是否具备 ApiResponse 的最小形状（有 status 字段）。
 * 这是 `assertSuccess` 用的内部守卫，对外请用 `isPlainObject` + 手动检查字段。
 */
function isApiResponseLike(
  x: unknown,
): x is { status?: unknown; message?: unknown; data?: unknown } {
  return isPlainObject(x);
}

/**
 * 统一处理 API 错误。
 * 根据 HTTP 状态码返回用户友好的错误信息。
 *
 * @throws Error 总是抛出错误（返回类型为 never）
 */
export function handleApiError(error: unknown, context: string): never {
  const status = isBackendError(error) ? error.response?.status : undefined;
  const errorData = isBackendError(error) ? error.response?.data : undefined;

  let errorMessage: string;

  switch (status) {
    case 401:
      errorMessage = '认证失败，请检查登录状态';
      break;
    case 403:
      errorMessage = '权限不足，无法执行此操作';
      break;
    case 404:
      errorMessage = '请求的资源不存在';
      break;
    case 409:
      errorMessage = errorData?.message || '资源冲突';
      break;
    case 422:
      errorMessage = getValidationErrorMessage(errorData) || '数据验证失败';
      break;
    case 500:
      errorMessage = '服务器内部错误，请稍后重试';
      break;
    default:
      errorMessage = getErrorMessage(error, context);
  }

  logger.error(`${context}: ${errorMessage}`, { status, error });
  throw new Error(errorMessage);
}

/**
 * 提取 FastAPI 422 验证错误的消息文本。
 */
function getValidationErrorMessage(
  errorData: { detail?: unknown; message?: string } | undefined,
): string | undefined {
  if (!errorData) return undefined;

  const detail = errorData.detail;

  if (Array.isArray(detail)) {
    return detail
      .map((err) => {
        if (typeof err !== 'object' || err === null) return '';
        const e = err as {
          loc?: string[];
          field?: string;
          msg?: string;
          message?: string;
        };
        const field = e.loc?.join('.') || e.field || '';
        const msg = e.msg || e.message || '';
        return field ? `${field}: ${msg}` : msg;
      })
      .filter(Boolean)
      .join('; ');
  }

  if (typeof detail === 'string') return detail;

  if (detail && typeof detail === 'object' && 'message' in detail) {
    const msg = (detail as { message?: unknown }).message;
    if (typeof msg === 'string') return msg;
  }

  return errorData.message;
}

/**
 * 安全地执行 API 调用并处理错误。
 * 封装 try-catch 逻辑，简化 service 函数。
 *
 * @example
 * export async function getUsers() {
 *   return safeApiCall(
 *     () => request<ApiResponse<User[]>>('/api/users'),
 *     '获取用户列表失败'
 *   );
 * }
 */
export async function safeApiCall<T>(
  apiCall: () => Promise<ApiResponse<T>>,
  context: string,
): Promise<T> {
  try {
    const response = await apiCall();
    assertSuccess(response, context);
    return response.data as T;
  } catch (error: unknown) {
    // 如果错误已经是我们主动抛出的业务 Error（不含 response 字段），直接重新抛出
    if (error instanceof Error && !isBackendError(error)) {
      throw error;
    }
    handleApiError(error, context);
  }
}
